import React, { useMemo } from 'react';
import { calculateFinancials } from '../../services/financialCalculations';
import { formatCurrency, formatPercentage } from '../../utils/formatters';

const ProjectIrrSensitivityTable = ({ inputs }) => {
    const { sensitivityData, waccRange, ownFundsRange } = useMemo(() => {
        const baseWacc = inputs.discountRate;
        const baseOwnFunds = inputs.ownFunds;

        const waccRange = [-0.02, -0.01, 0, 0.01, 0.02].map(mod => baseWacc + mod);
        const ownFundsRange = [-0.2, -0.1, 0, 0.1, 0.2].map(mod => baseOwnFunds * (1 + mod));

        const sensitivityData = ownFundsRange.map(funds => {
            return waccRange.map(wacc => {
                const tempInputs = { ...inputs, ownFunds: funds, discountRate: wacc };
                return calculateFinancials(tempInputs).indicators.projectIRR;
            });
        });

        return { sensitivityData, waccRange, ownFundsRange };
    }, [inputs]);

    return (
        <div className="mt-6">
            <h4 className="text-md font-semibold text-gray-800 dark:text-gray-100 mb-2">Project IRR Sensitivity Analysis (WACC vs. Own Funds)</h4>
            <div className="overflow-x-auto">
                <table className="min-w-full text-sm text-center border-collapse border border-gray-300 dark:border-gray-600">
                    <thead className="bg-gray-100 dark:bg-gray-700">
                        <tr>
                            <th className="p-2 border border-gray-300 dark:border-gray-600">
                                <div className="flex flex-col items-center">
                                    <span className="font-bold">Own Funds ↓</span>
                                    <span className="font-normal text-xs">WACC →</span>
                                </div>
                            </th>
                            {waccRange.map((wacc, index) => (
                                <th key={index} className="p-2 border border-gray-300 dark:border-gray-600 font-medium">{formatPercentage(wacc)}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {ownFundsRange.map((funds, rowIndex) => (
                            <tr key={rowIndex} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                                <th className="p-2 border border-gray-300 dark:border-gray-600 font-medium bg-gray-100 dark:bg-gray-700">{formatCurrency(funds)}</th>
                                {sensitivityData[rowIndex].map((irr, colIndex) => {
                                    const isBaseCase = rowIndex === 2 && colIndex === 2;
                                    return (
                                        <td key={colIndex} className={`p-2 border border-gray-300 dark:border-gray-600 ${isBaseCase ? 'bg-blue-100 dark:bg-blue-900/50 font-bold' : ''}`}>
                                            {formatPercentage(irr)}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 text-center">
                    Note: Project IRR is independent of the WACC (Discount Rate). The IRR values only change vertically with "Own Funds" because the financing structure affects the project's cash flows (e.g., interest payments).
                </p>
            </div>
        </div>
    );
};

export default ProjectIrrSensitivityTable; 